/**
 * FHIR R4 Bundle Validator — structural checks for correctness.
 * NOT a full FHIR validator — checks the basics that ensure interoperability.
 */

/** Loose shape used only for validation — mirrors the FHIR Bundle structure
 *  just enough to check required fields without casting to `any`. */
interface FhirBundleCandidate {
  resourceType?: string;
  type?: string;
  entry?: Array<{
    resource?: {
      resourceType?: string;
      id?: string;
      status?: string;
      code?: { coding?: Array<{ system?: string; code?: string }> };
      subject?: unknown;
      valueQuantity?: { value?: number | null; unit?: string };
      item?: unknown;
      name?: unknown[];
    };
  }>;
}

/** Validate a FHIR Bundle for structural correctness. */
export function validateBundle(bundle: Record<string, unknown> | null): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!bundle || typeof bundle !== 'object') {
    return { valid: false, errors: ['Bundle is null or not an object'] };
  }

  const b = bundle as FhirBundleCandidate;

  if (b.resourceType !== 'Bundle') {
    errors.push(`Bundle.resourceType must be "Bundle", got "${b.resourceType}"`);
  }

  if (b.type !== 'collection') {
    errors.push(`Bundle.type must be "collection", got "${b.type}"`);
  }

  if (!Array.isArray(b.entry)) {
    errors.push('Bundle.entry must be an array');
    return { valid: false, errors };
  }

  for (let i = 0; i < b.entry.length; i++) {
    const entry = b.entry[i];
    const prefix = `entry[${i}]`;

    if (!entry.resource) {
      errors.push(`${prefix}: missing resource`);
      continue;
    }

    const r = entry.resource;
    if (!r.resourceType || typeof r.resourceType !== 'string') {
      errors.push(`${prefix}: missing or invalid resourceType`);
    }

    // Observation-specific checks
    if (r.resourceType === 'Observation') {
      if (!r.status) errors.push(`${prefix} (Observation ${r.id}): missing status`);
      if (!r.code) {
        errors.push(`${prefix} (Observation ${r.id}): missing code`);
      } else if (!r.code.coding || !Array.isArray(r.code.coding) || r.code.coding.length === 0) {
        errors.push(`${prefix} (Observation ${r.id}): code.coding must be a non-empty array`);
      } else {
        for (const coding of r.code.coding) {
          if (!coding.system) errors.push(`${prefix} (Observation ${r.id}): code.coding missing system`);
          if (!coding.code) errors.push(`${prefix} (Observation ${r.id}): code.coding missing code`);
        }
      }
      if (!r.subject) errors.push(`${prefix} (Observation ${r.id}): missing subject`);
      if (r.valueQuantity) {
        if (r.valueQuantity.value == null) errors.push(`${prefix} (Observation ${r.id}): valueQuantity missing value`);
        if (!r.valueQuantity.unit) errors.push(`${prefix} (Observation ${r.id}): valueQuantity missing unit`);
      }
    }

    // QuestionnaireResponse checks
    if (r.resourceType === 'QuestionnaireResponse') {
      if (!r.status) errors.push(`${prefix} (QR ${r.id}): missing status`);
      if (!r.subject) errors.push(`${prefix} (QR ${r.id}): missing subject`);
      if (!Array.isArray(r.item)) errors.push(`${prefix} (QR ${r.id}): item must be an array`);
    }

    // DiagnosticReport checks
    if (r.resourceType === 'DiagnosticReport') {
      if (!r.status) errors.push(`${prefix} (DR ${r.id}): missing status`);
      if (!r.subject) errors.push(`${prefix} (DR ${r.id}): missing subject`);
    }

    // Patient checks
    if (r.resourceType === 'Patient') {
      if (!r.name || !Array.isArray(r.name) || r.name.length === 0) {
        errors.push(`${prefix} (Patient ${r.id}): missing name`);
      }
    }
  }

  return { valid: errors.length === 0, errors };
}
